/**
 * Review session state (implementation-plan.md §1.1, §D4).
 *
 * Owns the half of the session that lives at the review gate:
 *   - loading a session + its proposed clips back out of D1 (a coach can come back tomorrow)
 *   - clip decisions with OPTIMISTIC UI and a real rollback: the card moves the instant the
 *     coach taps, and if the worker refuses, the card moves back and says why
 *   - the counters the card exists to track: clip_count_proposed / clip_count_confirmed
 *   - the review → rendering handoff
 *
 * The session status itself (review → rendering → complete) stays in the session store, which is
 * the single source of truth shared with the capture screen.
 */
import { create } from 'zustand';
import {
  ApiError,
  completeReview,
  getSessionForReview,
  getVocabulary,
  updateClip,
  type ClipPatch,
  type ClipStatus,
  type SignedMedia,
  type StructuredClipRow,
  type TaggingSession,
  type VocabularyTerm,
} from '../api/client';
import {
  EMPTY_SUMMARY,
  batchConfirmTargets,
  reviewSummary,
  type ReviewSummary,
} from '../review';
import { useSessionStore } from './store';

/** Where the last reviewed session id lives, so /review restores it after a reload. */
export const REVIEW_SESSION_KEY = 'callout.reviewSessionId';

export function rememberReviewSession(sessionId: string | null): void {
  try {
    if (sessionId) window.localStorage.setItem(REVIEW_SESSION_KEY, sessionId);
    else window.localStorage.removeItem(REVIEW_SESSION_KEY);
  } catch {
    /* storage disabled — the URL query still carries the id */
  }
}

export function readRememberedReviewSession(): string | null {
  try {
    return window.localStorage.getItem(REVIEW_SESSION_KEY);
  } catch {
    return null;
  }
}

export interface ClipDecisionLog {
  clipId: string;
  action: 'confirm' | 'edit' | 'reject' | 'undo';
  at: string;
  ok: boolean;
  error?: string;
}

interface ReviewState {
  sessionId: string | null;
  session: TaggingSession | null;
  media: SignedMedia | null;
  sourceDuration: number | null;
  clips: StructuredClipRow[];
  vocabulary: VocabularyTerm[];
  vocabularyVersion: number | null;
  summary: ReviewSummary;
  /** Mirror of D1's TaggingSession counters — what the coach's own row says. */
  clipCountProposed: number;
  clipCountConfirmed: number;
  loading: boolean;
  error: string | null;
  /** Clip ids with a request in flight: the optimistic window the card renders as "saving". */
  pending: Record<string, true>;
  /** Last failure per clip, so the card can explain a rollback in place. */
  clipErrors: Record<string, string>;
  decisions: ClipDecisionLog[];
  renderRequested: boolean;
  renderError: string | null;

  load: (sessionId: string, token: string | null) => Promise<void>;
  decide: (clipId: string, patch: ClipPatch, token: string | null) => Promise<boolean>;
  confirmAll: (token: string | null) => Promise<number>;
  requestRender: (token: string | null) => Promise<boolean>;
  reset: () => void;
}

/**
 * The client's copy of the worker's status rule (workers/api/src/index.ts updateClipHandler):
 * an explicit status wins; otherwise an edit parks an unconfirmed clip in `edited` and leaves a
 * confirmed one confirmed. The worker's response REPLACES this guess, so the two cannot drift.
 */
export function applyPatch(clip: StructuredClipRow, patch: ClipPatch): StructuredClipRow {
  const next: StructuredClipRow = { ...clip };
  let hasEdits = false;
  if (patch.label !== undefined) {
    next.edited_label = patch.label;
    hasEdits = true;
  }
  if (patch.notes !== undefined) {
    next.edited_notes = patch.notes;
    hasEdits = true;
  }
  if (patch.tIn !== undefined) {
    next.edited_t_in = patch.tIn;
    hasEdits = true;
  }
  if (patch.tOut !== undefined) {
    next.edited_t_out = patch.tOut;
    hasEdits = true;
  }
  if (patch.vocabRef !== undefined) {
    next.vocab_ref = patch.vocabRef;
    next.edited_vocab_ref = patch.vocabRef;
    hasEdits = true;
  }
  if (patch.status !== undefined) next.status = patch.status as ClipStatus;
  else if (hasEdits) next.status = clip.status === 'confirmed' ? 'confirmed' : 'edited';
  return next;
}

/** Every clip the batch button would confirm, resolved one at a time so failures are attributable. */
export const batchTargets = batchConfirmTargets;

const initial = {
  sessionId: null,
  session: null,
  media: null,
  sourceDuration: null,
  clips: [] as StructuredClipRow[],
  vocabulary: [] as VocabularyTerm[],
  vocabularyVersion: null,
  summary: EMPTY_SUMMARY,
  clipCountProposed: 0,
  clipCountConfirmed: 0,
  loading: false,
  error: null,
  pending: {} as Record<string, true>,
  clipErrors: {} as Record<string, string>,
  decisions: [] as ClipDecisionLog[],
  renderRequested: false,
  renderError: null,
};

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}

export const useReviewStore = create<ReviewState>((set, get) => ({
  ...initial,

  load: async (sessionId, token) => {
    set({ loading: true, error: null, sessionId });
    try {
      const payload = await getSessionForReview(sessionId, token);
      const vocabulary = await getVocabulary(payload.session.sport || 'rugby-league', token).catch(
        () => null,
      );
      const clips = payload.clips ?? [];
      set({
        session: payload.session,
        media: payload.media,
        sourceDuration: payload.footage?.duration_seconds ?? null,
        clips,
        vocabulary: vocabulary?.terms ?? [],
        vocabularyVersion: vocabulary?.version ?? null,
        summary: reviewSummary(clips),
        clipCountProposed: payload.session.clip_count_proposed ?? clips.length,
        clipCountConfirmed: payload.session.clip_count_confirmed ?? 0,
        loading: false,
      });
      // The rest of the app (header counters, the render card) reads the shared session store.
      const session = useSessionStore.getState();
      session.setSession(payload.session.id);
      session.setStatus(payload.session.status === 'complete' ? 'complete' : 'review');
      rememberReviewSession(payload.session.id);
    } catch (error) {
      set({ loading: false, error: errorMessage(error) });
    }
  },

  decide: async (clipId, patch, token) => {
    const before = get().clips;
    const target = before.find((clip) => clip.id === clipId);
    if (!target) return false;
    const action: ClipDecisionLog['action'] =
      patch.status === 'confirmed' ? 'confirm' : patch.status === 'rejected' ? 'reject' : 'edit';

    // Optimistic: the coach's tap lands NOW; the card shows it as saving.
    const optimistic = before.map((clip) => (clip.id === clipId ? applyPatch(clip, patch) : clip));
    set((state) => ({
      clips: optimistic,
      summary: reviewSummary(optimistic),
      pending: { ...state.pending, [clipId]: true },
      clipErrors: Object.fromEntries(
        Object.entries(state.clipErrors).filter(([id]) => id !== clipId),
      ),
      decisions: [
        ...state.decisions,
        { clipId, action, at: new Date().toISOString(), ok: true },
      ],
    }));

    try {
      const result = await updateClip(clipId, patch, token);
      set((state) => {
        const clips = state.clips.map((clip) => (clip.id === clipId ? result.clip : clip));
        return {
          clips,
          summary: reviewSummary(clips),
          pending: Object.fromEntries(
            Object.entries(state.pending).filter(([id]) => id !== clipId),
          ) as Record<string, true>,
          clipCountProposed: result.clipCountProposed,
          clipCountConfirmed: result.clipCountConfirmed,
        };
      });
      return true;
    } catch (error) {
      const message = errorMessage(error);
      // ROLLBACK: the optimistic decision never happened. The card returns to its real state.
      set((state) => ({
        clips: before,
        summary: reviewSummary(before),
        pending: Object.fromEntries(
          Object.entries(state.pending).filter(([id]) => id !== clipId),
        ) as Record<string, true>,
        clipErrors: { ...state.clipErrors, [clipId]: message },
        decisions: [
          ...state.decisions.slice(0, -1),
          { clipId, action, at: new Date().toISOString(), ok: false, error: message },
        ],
      }));
      return false;
    }
  },

  confirmAll: async (token) => {
    const targets = batchTargets(get().clips);
    let confirmed = 0;
    for (const clip of targets) {
      // Sequential on purpose: one refusal must not take the rest of the batch with it, and the
      // worker's counters come back per clip.
      const ok = await get().decide(clip.id, { status: 'confirmed' }, token);
      if (ok) confirmed += 1;
    }
    return confirmed;
  },

  requestRender: async (token) => {
    const sessionId = get().sessionId;
    if (!sessionId) return false;
    set({ renderError: null });
    try {
      const result = await completeReview(sessionId, token);
      set({ renderRequested: true, session: result.session });
      const session = useSessionStore.getState();
      session.setStatus('rendering');
      session.noteTransition({
        from: 'review',
        to: 'rendering',
        at: new Date().toISOString(),
        reason: `review complete: ${result.review.clipCountConfirmed} confirmed`,
      });
      return true;
    } catch (error) {
      set({ renderError: errorMessage(error) });
      return false;
    }
  },

  reset: () => set({ ...initial }),
}));
